import type { AppRouter } from '@dentaly/server/trpc'
import { createTRPCReact, httpBatchLink } from '@trpc/react-query'
import superjson from 'superjson'
import { API_TOKEN, API_URL } from '@/config'
import { readDeskToken } from '@/lib/desk-session-storage'

export const trpc = createTRPCReact<AppRouter>()

/**
 * Get API token for authentication
 * Prioritizes localStorage over env variable
 *
 * Public web builds ship WITHOUT a token — staff auth uses the BetterAuth
 * session cookie and patients use the portal session cookie. The token header
 * is only sent when explicitly configured (local dev / LAN).
 */
function getApiToken(): string | undefined {
	// Check localStorage first (for user-provided token)
	const storedToken = localStorage.getItem('dentora_api_token')
	if (storedToken) return storedToken

	// Fall back to environment variable
	return API_TOKEN
}

export const trpcClient = trpc.createClient({
	links: [
		httpBatchLink({
			url: `${API_URL}/api/trpc`,
			transformer: superjson,
			// Send cookies (BetterAuth staff session / patient portal session)
			fetch(url, options) {
				return fetch(url, { ...options, credentials: 'include' })
			},
			headers() {
				const headers: Record<string, string> = {}
				const token = getApiToken()
				if (token) headers.Authorization = `Bearer ${token}`
				// Acute-case desk role (ZUB calendar drawer). Sent on every request
				// so the server can resolve it in createContext like the other
				// identities; procedures that don't care simply ignore it.
				const deskToken = readDeskToken()
				if (deskToken) headers['X-Desk-Session'] = deskToken
				return headers
			},
		}),
	],
})
